import { TELEGRAM_MOCK_MAP, type TelegramMockProfile } from '../config/telegram-mock'
import { resolveApiBaseUrl } from '../config/api-base-url'
import WebApp from '@twa-dev/sdk'
import type { RequestContactResponse } from '@twa-dev/types'

export type TmaAuthMode = 'mock' | 'real'

const defaultApiBaseUrl = resolveApiBaseUrl()

export interface TmaAuthUser {
  id: string
  role: 'student' | 'teacher'
  telegramId: string
  name: string
  flowNo?: string
}

export interface TmaAuthSession {
  token: string
  expiresIn: number
  user: TmaAuthUser
}

export interface TelegramAuthApiRequest {
  initData: string
  roleOverride?: 'student' | 'teacher'
}

export interface TelegramAuthApiSuccessResponse {
  token?: string
  access_token?: string
  expiresIn?: number
  user?: {
    id?: string | number
    telegramId?: string | number
    role?: 'student' | 'teacher'
    name?: string
    flowNo?: string
  }
}

export interface TelegramAuthApiErrorResponse {
  statusCode: 400 | 401 | 403
  message: string
}

export type TelegramAuthApiResponse = TelegramAuthApiSuccessResponse | TelegramAuthApiErrorResponse

export interface TelegramAuthContext {
  initData: string
  isTelegram: boolean
  telegramId?: string
  fallbackTelegramId?: string
  fallbackRole?: 'student' | 'teacher'
}

export type AuthAdapterResult =
  | {
      status: 'authenticated'
      session: TmaAuthSession
    }
  | {
      status: 'unauthorized'
      reason: string
      contractPreview?: TelegramAuthApiRequest
    }
  | {
      status: 'error'
      reason: string
      contractPreview?: TelegramAuthApiRequest
    }

interface AuthAdapter {
  authenticate(context: TelegramAuthContext): Promise<AuthAdapterResult>
}

const resolveMode = (): TmaAuthMode => (import.meta.env.VITE_TMA_AUTH_MODE === 'mock' ? 'mock' : 'real')

const isTruthyFlag = (value: string | undefined): boolean => {
  if (!value) {
    return false
  }

  return ['1', 'true', 'yes', 'on'].includes(value.trim().toLowerCase())
}

const allowBrowserMockFallback = (): boolean => {
  if (typeof import.meta.env.VITE_TMA_ALLOW_BROWSER_MOCK_FALLBACK === 'string') {
    return isTruthyFlag(import.meta.env.VITE_TMA_ALLOW_BROWSER_MOCK_FALLBACK)
  }

  return import.meta.env.DEV
}

const DEFAULT_ROLE_SWITCH_TELEGRAM_IDS = ['470227422', '1390927139']

const resolveRoleSwitchTelegramIds = (): Set<string> => {
  const raw = import.meta.env.VITE_TMA_ROLE_SWITCH_TELEGRAM_IDS
  const parsed = String(raw ?? '')
    .split(',')
    .map((value) => value.trim())
    .filter(Boolean)

  return new Set(parsed.length > 0 ? parsed : DEFAULT_ROLE_SWITCH_TELEGRAM_IDS)
}

const isRoleSwitchAllowedTelegramId = (telegramId: string): boolean => {
  return resolveRoleSwitchTelegramIds().has(telegramId.trim())
}

const normalizeBaseUrl = (value: string): string => {
  const trimmed = value.trim()
  if (!trimmed) {
    return 'http://localhost:4000'
  }

  return trimmed.endsWith('/') ? trimmed.slice(0, -1) : trimmed
}

const parseResponsePayload = async (response: Response): Promise<unknown> => {
  const text = await response.text()
  if (!text) {
    return undefined
  }

  try {
    return JSON.parse(text)
  } catch {
    return text
  }
}

const normalizePhoneToPlus7 = (value: string): string | null => {
  const digits = String(value || '').replace(/\D/g, '')
  if (!digits) {
    return null
  }

  if (digits.length === 11 && digits.startsWith('8')) {
    return `+7${digits.slice(1)}`
  }

  if (digits.length === 11 && digits.startsWith('7')) {
    return `+${digits}`
  }

  if (digits.length === 10) {
    return `+7${digits}`
  }

  return null
}

const extractPhoneFromContactResponse = (response?: RequestContactResponse): string | null => {
  if (!response || response.status !== 'sent') {
    return null
  }

  return response.responseUnsafe?.contact?.phone_number || null
}

const requestTelegramContactPhone = async (): Promise<string | null> => {
  try {
    if (typeof WebApp.requestContact !== 'function') {
      return null
    }

    return await new Promise<string | null>((resolve) => {
      let settled = false

      const finalize = (value: string | null) => {
        if (settled) {
          return
        }

        settled = true
        resolve(value)
      }

      const timeout = window.setTimeout(() => finalize(null), 15000)

      WebApp.requestContact((access, response) => {
        window.clearTimeout(timeout)
        if (!access) {
          finalize(null)
          return
        }

        const rawPhone = extractPhoneFromContactResponse(response)
        finalize(rawPhone ? normalizePhoneToPlus7(rawPhone) : null)
      })
    })
  } catch {
    return null
  }
}

const extractErrorMessage = (payload: unknown, fallback: string): string => {
  if (!payload) {
    return fallback
  }

  if (typeof payload === 'string') {
    return payload
  }

  if (typeof payload === 'object') {
    const maybeMessage = (payload as { message?: unknown }).message
    if (typeof maybeMessage === 'string' && maybeMessage.trim()) {
      return maybeMessage
    }

    if (Array.isArray(maybeMessage)) {
      return maybeMessage.map((item) => String(item)).join('; ')
    }
  }

  return fallback
}

const buildSessionFromTelegramAuthPayload = (payload: unknown): TmaAuthSession | null => {
  if (!payload || typeof payload !== 'object') {
    return null
  }

  const body = payload as TelegramAuthApiSuccessResponse
  const tokenRaw = body.token ?? body.access_token
  const token = typeof tokenRaw === 'string' ? tokenRaw.trim() : ''
  const user = body.user

  const role = user?.role === 'student' || user?.role === 'teacher' ? user.role : null
  const telegramId =
    user?.telegramId !== undefined && user?.telegramId !== null
      ? String(user.telegramId).trim()
      : user?.id !== undefined && user?.id !== null
        ? String(user.id).trim()
        : ''
  const id =
    user?.id !== undefined && user?.id !== null
      ? String(user.id).trim()
      : user?.telegramId !== undefined && user?.telegramId !== null
        ? String(user.telegramId).trim()
        : ''
  const name = typeof user?.name === 'string' ? user.name.trim() : ''

  if (!token || !role || !telegramId || !id || !name) {
    return null
  }

  return {
    token,
    expiresIn: typeof body.expiresIn === 'number' ? body.expiresIn : 3600,
    user: {
      id,
      role,
      telegramId,
      name,
      flowNo: typeof user?.flowNo === 'string' ? user.flowNo : undefined,
    },
  }
}

const resolveTelegramId = (context: TelegramAuthContext): string | undefined => {
  const direct = context.telegramId?.trim()
  if (direct) {
    return direct
  }

  const fallback = context.fallbackTelegramId?.trim()
  if (fallback) {
    return fallback
  }

  // URL param for browser fallback mock
  if (typeof window !== 'undefined') {
    const params = new URLSearchParams(window.location.search)
    return params.get('mockTelegramId') || undefined
  }

  return undefined
}

const resolveRoleOverride = (context: TelegramAuthContext): 'student' | 'teacher' | undefined => {
  if (context.fallbackRole === 'student' || context.fallbackRole === 'teacher') {
    return context.fallbackRole
  }

  const telegramId = resolveTelegramId(context)
  if (telegramId && isRoleSwitchAllowedTelegramId(telegramId)) {
    return 'teacher'
  }

  return undefined
}

const buildMockSession = (
  telegramId: string,
  roleOverride?: 'student' | 'teacher',
): TmaAuthSession | undefined => {
  const profile = TELEGRAM_MOCK_MAP[telegramId]
  if (!profile) {
    return undefined
  }

  const effectiveRole = roleOverride ?? profile.role

  const payload = {
    sub: profile.id,
    role: effectiveRole,
    telegramId: profile.telegramId,
  }

  return {
    token: `mock.${btoa(JSON.stringify(payload))}.cycle1`,
    expiresIn: 3600,
    user: {
      id: profile.id,
      role: effectiveRole,
      telegramId: profile.telegramId,
      name: profile.name,
      flowNo: profile.flowNo,
    },
  }
}

const buildSessionFromMockAuthPayload = (
  payload: unknown,
  fallbackProfile: TelegramMockProfile,
): TmaAuthSession | null => {
  if (!payload || typeof payload !== 'object') {
    return null
  }

  const body = payload as Record<string, unknown>
  const tokenRaw = body.access_token ?? body.token
  const token = typeof tokenRaw === 'string' ? tokenRaw.trim() : ''

  const apiUser =
    body.user && typeof body.user === 'object'
      ? (body.user as Record<string, unknown>)
      : null

  const roleRaw = typeof apiUser?.role === 'string' ? apiUser.role : fallbackProfile.role
  const role = roleRaw === 'student' || roleRaw === 'teacher' ? roleRaw : null

  const telegramIdRaw =
    apiUser?.telegramId !== undefined && apiUser?.telegramId !== null
      ? String(apiUser.telegramId).trim()
      : fallbackProfile.telegramId

  const idRaw =
    apiUser?.id !== undefined && apiUser?.id !== null
      ? String(apiUser.id).trim()
      : fallbackProfile.id

  const nameRaw = typeof apiUser?.name === 'string' ? apiUser.name.trim() : fallbackProfile.name
  const flowNoRaw =
    typeof apiUser?.flowNo === 'string' && apiUser.flowNo.trim()
      ? apiUser.flowNo.trim()
      : fallbackProfile.flowNo

  if (!token || !role || !telegramIdRaw || !idRaw || !nameRaw) {
    return null
  }

  return {
    token,
    expiresIn: 3600,
    user: {
      id: idRaw,
      role,
      telegramId: telegramIdRaw,
      name: nameRaw,
      flowNo: flowNoRaw,
    },
  }
}

class MockTelegramAuthAdapter implements AuthAdapter {
  private readonly baseUrl = normalizeBaseUrl(defaultApiBaseUrl)

  async authenticate(context: TelegramAuthContext): Promise<AuthAdapterResult> {
    const telegramId = resolveTelegramId(context)
    if (!telegramId) {
      return {
        status: 'unauthorized',
        reason: 'Не найден telegramId для mock mapping.',
      }
    }

    const roleOverride = resolveRoleOverride(context)
    const fallbackSession = buildMockSession(telegramId, roleOverride)
    if (!fallbackSession) {
      return {
        status: 'unauthorized',
        reason: `Профиль telegramId=${telegramId} отсутствует в TELEGRAM_MOCK_MAP.`,
      }
    }

    const fallbackProfile: TelegramMockProfile = {
      id: fallbackSession.user.id,
      role: fallbackSession.user.role,
      telegramId: fallbackSession.user.telegramId,
      name: fallbackSession.user.name,
      flowNo: fallbackSession.user.flowNo,
    }

    try {
      const response = await fetch(`${this.baseUrl}/auth/mock`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          telegramId,
          role: roleOverride ?? fallbackProfile.role,
          name: fallbackProfile.name,
          flowNo: fallbackProfile.flowNo,
        }),
      })

      const payload = await parseResponsePayload(response)

      if (response.ok) {
        const session = buildSessionFromMockAuthPayload(payload, fallbackProfile)
        if (!session) {
          return {
            status: 'error',
            reason: 'Ответ /auth/mock не соответствует ожидаемому контракту session payload.',
          }
        }

        return {
          status: 'authenticated',
          session,
        }
      }

      const message = extractErrorMessage(payload, `HTTP ${response.status}`)
      return {
        status: 'error',
        reason: `Mock auth endpoint error (${response.status}): ${message}`,
      }
    } catch (error) {
      return {
        status: 'error',
        reason: `Сбой сети при вызове POST /auth/mock: ${error instanceof Error ? error.message : String(error)}`,
      }
    }
  }
}

class RealTelegramAuthAdapter implements AuthAdapter {
  private readonly mockAdapter = new MockTelegramAuthAdapter()

  private readonly baseUrl = normalizeBaseUrl(defaultApiBaseUrl)

  private readonly browserFallbackEnabled = allowBrowserMockFallback()

  async authenticate(context: TelegramAuthContext): Promise<AuthAdapterResult> {
    const roleOverride = resolveRoleOverride(context)

    const request: TelegramAuthApiRequest = {
      initData: context.initData?.trim() || '',
      roleOverride,
    }

    if (!request.initData) {
      if (!context.isTelegram && this.browserFallbackEnabled) {
        const fallbackResult = await this.mockAdapter.authenticate(context)
        if (fallbackResult.status === 'authenticated') {
          return fallbackResult
        }

        return {
          status: 'unauthorized',
          reason: `Browser fallback включён, но mock auth не прошёл: ${fallbackResult.reason}`,
        }
      }

      return {
        status: 'unauthorized',
        reason:
          'Отсутствует валидный Telegram context: initData пустой. Для browser fallback включите VITE_TMA_ALLOW_BROWSER_MOCK_FALLBACK и передайте mockTelegramId.',
        contractPreview: request,
      }
    }

    const url = `${this.baseUrl}/auth/telegram`

    try {
      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(request),
      })

      const payload = await parseResponsePayload(response)

      if (response.ok) {
        const session = buildSessionFromTelegramAuthPayload(payload)
        if (!session) {
          const body = payload && typeof payload === 'object' ? (payload as Record<string, unknown>) : undefined
          const user =
            body?.user && typeof body.user === 'object'
              ? (body.user as Record<string, unknown>)
              : undefined
          return {
            status: 'error',
            reason: `Ответ /auth/telegram не соответствует ожидаемому контракту session payload. keys: token=${String(
              body?.token !== undefined,
            )}, access_token=${String(body?.access_token !== undefined)}, user.id=${String(
              user?.id !== undefined,
            )}, user.telegramId=${String(user?.telegramId !== undefined)}, user.role=${String(
              user?.role !== undefined,
            )}, user.name=${String(user?.name !== undefined)}`,
            contractPreview: request,
          }
        }

        return {
          status: 'authenticated',
          session,
        }
      }

      if (response.status === 404 && context.isTelegram) {
        const phone = await requestTelegramContactPhone()
        if (!phone) {
          return {
            status: 'unauthorized',
            reason:
              'Ваш Telegram ID не зарегистрирован. Разрешите доступ к номеру телефона, чтобы завершить привязку.',
            contractPreview: request,
          }
        }

        const linkResponse = await fetch(`${this.baseUrl}/auth/telegram/link-by-phone`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            initData: request.initData,
            phone,
            roleOverride,
          }),
        })

        const linkPayload = await parseResponsePayload(linkResponse)

        if (linkResponse.ok) {
          const session = buildSessionFromTelegramAuthPayload(linkPayload)
          if (!session) {
            return {
              status: 'error',
              reason:
                'Ответ /auth/telegram/link-by-phone не соответствует ожидаемому контракту session payload.',
              contractPreview: request,
            }
          }

          return {
            status: 'authenticated',
            session,
          }
        }

        const linkErrorMessage = extractErrorMessage(
          linkPayload,
          `HTTP ${linkResponse.status}`,
        )

        if ([400, 401, 403, 404, 409].includes(linkResponse.status)) {
          return {
            status: 'unauthorized',
            reason: `Привязка по телефону отклонена (${linkResponse.status}): ${linkErrorMessage}`,
            contractPreview: request,
          }
        }

        return {
          status: 'error',
          reason: `Auth link-by-phone endpoint error (${linkResponse.status}): ${linkErrorMessage}`,
          contractPreview: request,
        }
      }

      const message = extractErrorMessage(payload, `HTTP ${response.status}`)

      if ([400, 401, 403].includes(response.status)) {
        return {
          status: 'unauthorized',
          reason: `Telegram auth отклонён (${response.status}): ${message}`,
          contractPreview: request,
        }
      }

      return {
        status: 'error',
        reason: `Telegram auth endpoint error (${response.status}): ${message}`,
        contractPreview: request,
      }
    } catch (error) {
      return {
        status: 'error',
        reason: `Сбой сети при вызове POST /auth/telegram: ${error instanceof Error ? error.message : String(error)}`,
        contractPreview: request,
      }
    }
  }
}

const createAdapter = (mode: TmaAuthMode): AuthAdapter => {
  if (mode === 'real') {
    return new RealTelegramAuthAdapter()
  }

  return new MockTelegramAuthAdapter()
}

export interface TmaAuthService {
  mode: TmaAuthMode
  authenticate(context: TelegramAuthContext): Promise<AuthAdapterResult>
}

export const createTmaAuthService = (mode: TmaAuthMode = resolveMode()): TmaAuthService => {
  const adapter = createAdapter(mode)

  return {
    mode,
    authenticate: (context) => adapter.authenticate(context),
  }
}
